import { useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { Send, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // "success" | "error" | null

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus(null);

    try {
      // Send via EmailJS (fail silently if credentials are invalid)
      if (SERVICE_ID && TEMPLATE_ID && PUBLIC_KEY) {
        try {
          await emailjs.send(SERVICE_ID, TEMPLATE_ID, {
            name: form.name,
            email: form.email,
            company: form.company,
            message: form.message,
          }, PUBLIC_KEY);
        } catch (emailErr) {
          console.warn("EmailJS send failed (non-critical):", emailErr);
        }
      }

      // Persist to backend
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        throw new Error(`Backend error: ${response.status}`);
      }

      setStatus("success");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      console.error("Contact form error:", err);
      setStatus("error");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#0A0A0B] relative grain-overlay"
    >
      <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#E5A97A]/80 block mb-4">
            Get In Touch
          </span>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white mb-4">
            Let&apos;s Secure Your Business
          </h2>
          <p className="text-base text-zinc-400">
            Tell us about your security needs and we&apos;ll get back to you within 24 hours.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="contact-form-wrapper rounded-2xl p-8 md:p-10"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="text-sm font-medium text-zinc-300 block mb-2">
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                  className="bg-black/50 border-white/10 focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] text-white placeholder-zinc-600 h-11"
                />
              </div>
              <div>
                <label htmlFor="email" className="text-sm font-medium text-zinc-300 block mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="you@company.com"
                  className="bg-black/50 border-white/10 focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] text-white placeholder-zinc-600 h-11"
                />
              </div>
            </div>

            <div>
              <label htmlFor="company" className="text-sm font-medium text-zinc-300 block mb-2">
                Company
              </label>
              <Input
                id="company"
                name="company"
                data-testid="contact-company-input"
                value={form.company}
                onChange={handleChange}
                placeholder="Your company name"
                className="bg-black/50 border-white/10 focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] text-white placeholder-zinc-600 h-11"
              />
            </div>

            <div>
              <label htmlFor="message" className="text-sm font-medium text-zinc-300 block mb-2">
                Message
              </label>
              <Textarea
                id="message"
                name="message"
                data-testid="contact-message-input"
                value={form.message}
                onChange={handleChange}
                required
                rows={5}
                placeholder="Tell us about your security needs..."
                className="bg-black/50 border-white/10 focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] text-white placeholder-zinc-600 resize-none"
              />
            </div>

            <Button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="w-full bg-[#B87333] hover:bg-[#A6632B] text-white rounded-md py-3 h-12 text-sm font-medium transition-all disabled:opacity-50"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>

            {status === "success" && (
              <p data-testid="contact-success-message" className="text-center text-sm text-emerald-400">
                Message sent successfully! We&apos;ll get back to you soon.
              </p>
            )}
            {status === "error" && (
              <p data-testid="contact-error-message" className="text-center text-sm text-red-400">
                Something went wrong. Please try again or email us directly.
              </p>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
}
